import * as Schema from "effect/Schema";
import * as DateTime from "effect/DateTime";
import * as Option from "effect/Option";
import { OperatorProjectId } from "./baseSchemas.ts";

const Id = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(512));
const Name = Schema.String.check(Schema.isPattern(/^[a-zA-Z0-9_./:@-]{1,256}$/u));
const Count = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const Timestamp = Schema.String.check(
  Schema.isPattern(
    /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/u,
  ),
)
  .check(
    Schema.makeFilter((value) => {
      const date = DateTime.make(`${value.slice(0, 10)}T00:00:00.000Z`);
      return (
        Option.isSome(date) && DateTime.formatIso(date.value).slice(0, 10) === value.slice(0, 10)
      );
    }),
  )
  .annotate({ format: "date-time" });

export const WorkspaceLogOutcome = Schema.Literals([
  "started",
  "accepted",
  "succeeded",
  "failed",
  "rejected",
  "cancelled",
  "uncertain",
]);
export type WorkspaceLogOutcome = typeof WorkspaceLogOutcome.Type;

/** Metadata only. Producers must never pass prompts, arguments, results, or error messages. */
export const WorkspaceLogDetails = Schema.Struct({
  tool: Schema.optionalKey(Id),
  command: Schema.optionalKey(Name),
  purposeId: Schema.optionalKey(Id),
  requestId: Schema.optionalKey(Id),
  assignmentId: Schema.optionalKey(Id),
  deliveryId: Schema.optionalKey(Id),
  approvalId: Schema.optionalKey(Id),
  evidenceId: Schema.optionalKey(Id),
  resourceId: Schema.optionalKey(Id),
  revision: Schema.optionalKey(Id),
  state: Schema.optionalKey(Name),
  provider: Schema.optionalKey(Name),
  model: Schema.optionalKey(Id),
  inputTokens: Schema.optionalKey(Count),
  outputTokens: Schema.optionalKey(Count),
  durationMs: Schema.optionalKey(Count),
  errorCode: Schema.optionalKey(Name),
  retentionDays: Schema.optionalKey(Count),
  count: Schema.optionalKey(Count),
});

export const WorkspaceLogInput = Schema.Struct({
  eventKey: Id,
  projectId: OperatorProjectId,
  conversationId: Schema.optionalKey(Id),
  loopId: Schema.optionalKey(Id),
  runId: Schema.optionalKey(Id),
  workflowId: Schema.optionalKey(Id),
  operationId: Id,
  parentOperationId: Schema.optionalKey(Id),
  actorId: Id,
  actorKind: Schema.Literals(["user", "agent", "system", "integration"]),
  source: Schema.Literals([
    "app",
    "slack",
    "teams",
    "email",
    "communication",
    "mcp",
    "loop",
    "worker",
    "system",
  ]),
  visibility: Schema.Literals(["workspace", "conversation", "loop", "actor"]),
  eventName: Name,
  outcome: WorkspaceLogOutcome,
  occurredAt: Schema.optionalKey(Timestamp),
  traceId: Schema.optionalKey(Id),
  spanId: Schema.optionalKey(Id),
  details: WorkspaceLogDetails,
});
export type WorkspaceLogInput = typeof WorkspaceLogInput.Type;

export const WorkspaceLogEvent = Schema.Struct({
  ...WorkspaceLogInput.fields,
  schemaVersion: Schema.Literal(1),
  eventId: Id,
  environmentId: Id,
  sequence: Count,
  occurredAt: Timestamp,
  recordedAt: Timestamp,
});
export type WorkspaceLogEvent = typeof WorkspaceLogEvent.Type;

export const WorkspaceLogQuery = Schema.Struct({
  projectId: OperatorProjectId,
  conversationId: Schema.optionalKey(Id),
  loopId: Schema.optionalKey(Id),
  workflowId: Schema.optionalKey(Id),
  runId: Schema.optionalKey(Id),
  operationId: Schema.optionalKey(Id),
  actorId: Schema.optionalKey(Id),
  eventName: Schema.optionalKey(Name),
  outcome: Schema.optionalKey(WorkspaceLogOutcome),
  from: Schema.optionalKey(Timestamp),
  to: Schema.optionalKey(Timestamp),
  limit: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 200 }))),
  cursor: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(8192))),
});
export type WorkspaceLogQuery = typeof WorkspaceLogQuery.Type;
const { projectId: _projectId, ...filterFields } = WorkspaceLogQuery.fields;
export const WorkspaceLogFilters = Schema.Struct(filterFields);

export const WorkspaceLogPage = Schema.Struct({
  events: Schema.Array(WorkspaceLogEvent),
  pageCursor: Schema.optionalKey(Schema.String),
  nextCursor: Schema.optionalKey(Schema.String),
  upperSequence: Count,
});
export type WorkspaceLogPage = typeof WorkspaceLogPage.Type;

export const WorkspaceLogPolicy = Schema.Struct({
  projectId: OperatorProjectId,
  retentionDays: Schema.Int.check(Schema.isBetween({ minimum: 7, maximum: 3650 })),
});
export type WorkspaceLogPolicy = typeof WorkspaceLogPolicy.Type;
export const WorkspaceLogPolicyStatus = Schema.Struct({
  ...WorkspaceLogPolicy.fields,
  canManage: Schema.Boolean,
});

export const WorkspaceLogExport = Schema.Struct({
  ndjson: Schema.String,
  nextCursor: Schema.optionalKey(Schema.String),
  manifest: Schema.Struct({
    schemaVersion: Schema.Literal(1),
    environmentId: Id,
    projectId: OperatorProjectId,
    upperSequence: Count,
    count: Count,
    sha256: Schema.String,
  }),
});
export type WorkspaceLogExport = typeof WorkspaceLogExport.Type;

export class WorkspaceLogError extends Schema.TaggedErrorClass<WorkspaceLogError>()(
  "WorkspaceLogError",
  {
    code: Schema.Literals([
      "invalid_input",
      "invalid_cursor",
      "conflict",
      "forbidden",
      "storage",
      "outcome_unknown",
    ]),
    message: Schema.String,
  },
) {}
